// Globals
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Color } from 'antd/es/color-picker';

// Components
import { FormItem, IFormInstance, useWatchForm } from '@/components';
import { ColorPicker, Input, TextArea, TextEditor } from '@/ui';
import AiBotPreview from '../AiBotPreview/AiBotPreview';
import {
  StyledForm,
  StyledFormItem,
  StyledPreviewColumn,
  StyledPreviewTitle,
  StyledSettingsCard,
  StyledSettingsTitle,
} from './AiBotConfigFormStyles';

// Helpers
import { AiBotConfigFormFields, TAiBotConfigFormValues } from '../../helpers';
import { useAiBotConfigFormRules } from '../../hooks';

type TAiBotConfigFormProps = {
  onSubmit: (values: TAiBotConfigFormValues) => void;
  initialValues?: Partial<TAiBotConfigFormValues>;
  isLoading?: boolean;
  form: IFormInstance;
};

const AiBotConfigForm: FC<TAiBotConfigFormProps> = ({ onSubmit, initialValues, isLoading, form }) => {
  const { t } = useTranslation();
  const rules = useAiBotConfigFormRules();

  const senderName = useWatchForm<string>(AiBotConfigFormFields.SENDER_NAME, form);
  const mainColor = useWatchForm<Color>(AiBotConfigFormFields.MAIN_COLOR, form);
  const welcomeMessage = useWatchForm<string>(AiBotConfigFormFields.WELCOME_MESSAGE, form);
  const disclaimerMessage = useWatchForm<string>(AiBotConfigFormFields.DISCLAIMER_MESSAGE, form);

  return (
    <StyledForm form={form} initialValues={initialValues} onFinish={onSubmit} disabled={isLoading}>
      <StyledSettingsCard>
        <StyledSettingsTitle>{t('title_ai_bot_settings')}</StyledSettingsTitle>
        <StyledFormItem
          label={t('label_sender_name')}
          name={AiBotConfigFormFields.SENDER_NAME}
          rules={rules[AiBotConfigFormFields.SENDER_NAME]}
          extra={t('text_ai_bot_sender_name_helper')}
        >
          <Input placeholder={t('text_ai_bot_default_sender_name')} />
        </StyledFormItem>
        <StyledFormItem
          label={t('label_main_color')}
          name={AiBotConfigFormFields.MAIN_COLOR}
          rules={rules[AiBotConfigFormFields.MAIN_COLOR]}
          extra={t('text_ai_bot_main_color_helper')}
        >
          <ColorPicker disabledAlpha showText />
        </StyledFormItem>
        <FormItem
          label={t('label_welcome_message')}
          name={AiBotConfigFormFields.WELCOME_MESSAGE}
          rules={rules[AiBotConfigFormFields.WELCOME_MESSAGE]}
          extra={t('text_ai_bot_welcome_message_helper')}
        >
          <TextEditor disableLinksAndImages placeholder={t('text_ai_bot_default_welcome_message')} />
        </FormItem>
        <StyledFormItem
          label={t('label_disclaimer_message')}
          name={AiBotConfigFormFields.DISCLAIMER_MESSAGE}
          rules={rules[AiBotConfigFormFields.DISCLAIMER_MESSAGE]}
          extra={t('text_ai_bot_disclaimer_message_helper')}
        >
          <TextArea rows={2} placeholder={t('text_ai_bot_default_disclaimer_message')} />
        </StyledFormItem>
      </StyledSettingsCard>
      <StyledPreviewColumn>
        <StyledPreviewTitle>{t('title_preview')}</StyledPreviewTitle>
        <AiBotPreview
          color={mainColor}
          senderName={senderName}
          welcomeMessage={welcomeMessage}
          disclaimerMessage={disclaimerMessage}
        />
      </StyledPreviewColumn>
    </StyledForm>
  );
};

export default AiBotConfigForm;
